import type { ReactNode } from "react";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Icon,
  Skeleton,
  type IconName,
} from "@diametral/design-system/react";

import { useTranslation } from "../lib/i18n";
import { messageOf } from "../lib/toast";

/** Placeholder bars where content will land (first load only, never over stale data). */
export function LoadingBlock({ lines = 4, label }: { lines?: number; label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="ada-stack ada-stack--tight" aria-busy="true" role="status">
      <span className="sr-only">{label ?? t("Loading…")}</span>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} style={{ height: i === 0 ? 28 : 18, width: i === 0 ? "40%" : `${90 - i * 8}%` }} />
      ))}
    </div>
  );
}

export function ErrorBlock({
  error,
  onRetry,
  title,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  const { t } = useTranslation();
  return (
    <Alert tone="danger">
      <AlertTitle>{title ?? t("Could not load")}</AlertTitle>
      <AlertDescription>{messageOf(error)}</AlertDescription>
      {onRetry ? (
        <AlertAction>
          <Button size="sm" variant="outline" onClick={onRetry}>
            {t("Retry")}
          </Button>
        </AlertAction>
      ) : null}
    </Alert>
  );
}

export function EmptyBlock({
  title,
  description,
  icon = "list",
  children,
}: {
  title: string;
  description?: ReactNode;
  icon?: IconName;
  children?: ReactNode;
}) {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon name={icon} aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description ? <EmptyDescription>{description}</EmptyDescription> : null}
      </EmptyHeader>
      {children ? <EmptyContent>{children}</EmptyContent> : null}
    </Empty>
  );
}
